import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Camera, CheckCircle } from "lucide-react";

interface DirectScreenshotCaptureProps {
  county: string;
  onRecordSaved?: () => void;
}

export function DirectScreenshotCapture({ county, onRecordSaved }: DirectScreenshotCaptureProps) {
  const [imageData, setImageData] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState<any>(null);

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const blob = items[i].getAsFile();
        if (blob) {
          const reader = new FileReader();
          reader.onload = (event) => {
            const base64 = event.target?.result as string;
            setImageData(base64);
          };
          reader.readAsDataURL(blob);
        }
      }
    }
  };

  const handleClipboardButton = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        const imageType = item.types.find(t => t.startsWith("image/"));
        if (imageType) {
          const blob = await item.getType(imageType);
          const reader = new FileReader();
          reader.onload = (event) => {
            const base64 = event.target?.result as string;
            setImageData(base64);
          };
          reader.readAsDataURL(blob);
          return;
        }
      }
      toast.error("No image found in clipboard");
    } catch {
      toast.error("Clipboard access denied — use Ctrl+V instead");
    }
  };

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && imageData && !isProcessing) {
        e.preventDefault();
        // Trigger the extract button click
        document.getElementById("extract-save-btn")?.click();
      }
    };
    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, [imageData, isProcessing]);

  const handleExtractAndSave = async () => {
    if (!imageData) {
      toast.error("Please paste a screenshot first");
      return;
    }

    setIsProcessing(true);
    setExtractedData(null);

    try {
      const { data, error } = await supabase.functions.invoke("extract-contract-screenshot", {
        body: {
          imageData,
          county,
        },
      });

      if (error) throw error;

      if (data.success) {
        toast.success("Contract data extracted and saved!");
        setExtractedData(data.extractedData);
        onRecordSaved?.();
        
        // Clear for next capture
        setTimeout(() => {
          setImageData(null);
          setExtractedData(null);
        }, 3000);
      } else {
        throw new Error(data.error || "Failed to extract data");
      }
    } catch (error: any) {
      console.error("Extract error:", error);
      toast.error(error.message || "Failed to extract contract data");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Camera className="h-5 w-5" />
          Direct Screenshot Research Bot
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Paste a screenshot of a land contract document and it will automatically extract all fields and save to the database.
        </p>

        <div
          className="border-2 border-dashed border-border rounded-lg p-8 text-center focus:ring-2 focus:ring-ring focus:outline-none"
          onPaste={handlePaste}
          tabIndex={0}
        >
          {!imageData ? (
            <div className="space-y-2">
              <Camera className="h-12 w-12 mx-auto text-muted-foreground" />
              <p className="text-muted-foreground mb-3">
                Click here and press <kbd className="px-2 py-1 bg-muted rounded">Ctrl+V</kbd> to paste, or:
              </p>
              <Button variant="outline" size="sm" onClick={handleClipboardButton}>
                📋 Paste from Clipboard
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <img
                src={imageData}
                alt="Pasted screenshot"
                className="max-h-96 mx-auto rounded border"
              />
              <div className="flex gap-2 justify-center">
                <Button
                  id="extract-save-btn"
                  onClick={handleExtractAndSave}
                  disabled={isProcessing}
                  size="lg"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Extracting Data...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Extract & Save (Enter)
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setImageData(null)}
                  disabled={isProcessing}
                >
                  Clear
                </Button>
              </div>
            </div>
          )}
        </div>

        {extractedData && (
          <div className="mt-4 p-4 bg-muted rounded-lg">
            <h4 className="font-semibold mb-2 text-green-600 flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Successfully Extracted & Saved:
            </h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="font-medium">Document #:</span> {extractedData.document_number}</div>
              <div><span className="font-medium">Filing Date:</span> {extractedData.filing_date}</div>
              <div><span className="font-medium">Grantor:</span> {extractedData.grantor_name}</div>
              <div><span className="font-medium">Grantee:</span> {extractedData.grantee_name}</div>
              <div><span className="font-medium">Property:</span> {extractedData.property_address}</div>
              <div><span className="font-medium">Loan Amount:</span> ${extractedData.loan_amount?.toLocaleString()}</div>
              <div><span className="font-medium">Interest Rate:</span> {extractedData.interest_rate}%</div>
              <div><span className="font-medium">Monthly Payment:</span> ${extractedData.monthly_payment?.toLocaleString()}</div>
            </div>
          </div>
        )}
      </Card>

      <div className="text-xs text-muted-foreground">
        <p><strong>Instructions:</strong></p>
        <ol className="list-decimal list-inside space-y-1 mt-2">
          <li>Take a screenshot of the land contract document (Win+Shift+S or Cmd+Shift+4)</li>
          <li>Click in the paste area above</li>
          <li>Press Ctrl+V (or Cmd+V) to paste the screenshot</li>
          <li>Click "Extract & Save" to automatically extract all fields and save to database</li>
        </ol>
      </div>
    </div>
  );
}
